import { useCallback, useEffect, useRef, useState } from 'react';
import { SpeakerSegment } from '../../types';
import type { LiveSource, LiveTranscriptEvent, NativePauseChange, TranscribeLanguage } from '../../global';
import { t } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';

/**
 * Konuşmacı etiketleri. Ayrım kişi bazlı değil kaynak bazlı: mikrofondan
 * gelen "Sen", hoparlörden (sistem sesi) gelen herkes "Katılımcı". Gerçek
 * diarization ağır bir ML bağımlılığı gerektirdiği için bilerek yok.
 * Etiketler veride Türkçe kalıyor (yapay zekâ istemi bunlara dayanıyor);
 * arayüzde `displaySpeaker` ile seçili dile çevriliyor.
 */
export const SPEAKER_ME = 'Sen';
export const SPEAKER_GUEST = 'Katılımcı';

export interface RecordingResult {
  filePath: string;
  durationSeconds: number;
  transcript: SpeakerSegment[];
  /** Kaydın başladığı ve bittiği an (duvar saati; duraklatmalar dahil). */
  startedAtISO: string;
  endedAtISO: string;
}

/** Henüz tamamlanmamış, ekranda soluk gösterilen cümle. */
export interface PendingLine {
  speaker: string;
  text: string;
}

/**
 * Sessizlik ölçümü — otomatik durdurma (bkz. useAutoStop) toplantının bitip
 * bitmediğini buna bakarak tahmin ediyor. Süreler duvar saatiyle ölçülüyor;
 * duraklatılmış süre sessizlik sayılmıyor.
 */
export interface SilenceSnapshot {
  micSilentMs: number;
  systemSilentMs: number;
  /**
   * Bu kayıtta karşı taraftan (sistem sesi) kayda değer süre ses geldi mi?
   * Gelmediyse ya yüz yüze bir toplantıdır ya da görüşme henüz başlamamıştır —
   * "karşı taraf sustu" kuralları bu durumda anlamsız.
   */
  systemEverHeard: boolean;
}

export interface StartOptions {
  /**
   * Söz olarak da verilebilir: web'de ekran paylaşımı penceresi yalnızca
   * tıklamanın içinde açılıyor, yani akışlar dil tercihi beklenmeden istenmeli.
   */
  language: TranscribeLanguage | Promise<TranscribeLanguage>;
  /**
   * Yalnızca web: mikrofonun yanında paylaşılan toplantı sekmesinin sesi de
   * alınsın mı. Masaüstünde sistem sesi her zaman alınıyor.
   */
  captureSystemAudio?: boolean;
  /**
   * Mikrofon alınmasın, yalnızca sistem sesi (web'de paylaşılan sekmenin
   * sesi) — takvim kartındaki mikrofon düğmesi. Sistem sesi alınamazsa kayıt
   * başlamaz (kaydedilecek başka ses yok).
   */
  systemOnly?: boolean;
}

interface UseRecorderResult {
  isRecording: boolean;
  isPaused: boolean;
  seconds: number;
  /** 0-100, yalnızca mikrofon. */
  micLevel: number;
  /** 0-100, yalnızca sistem sesi (karşı taraf). */
  systemLevel: number;
  /** Karşı tarafın sesi alınıyor mu (web'de yalnızca sekme paylaşılırsa). */
  hasSystemAudio: boolean;
  error: string | null;
  /** Kaydı etkilemeyen bilgi notu (ör. kota takviyesi verildi). */
  notice: string | null;
  /** Tamamlanmış cümleler, kronolojik. */
  liveTranscript: SpeakerSegment[];
  /** Oluşmakta olan cümleler (konuşmacı başına en fazla bir tane). */
  pendingLines: PendingLine[];
  start: (options: StartOptions) => Promise<void>;
  stop: () => Promise<RecordingResult | null>;
  discard: () => Promise<void>;
  togglePause: () => void;
  /** Kayıt sürerken dil değiştirilebilir; Deepgram bağlantıları yeni dille yeniden açılır. */
  setLanguage: (language: TranscribeLanguage) => void;
  /** Anlık sessizlik durumu — render tetiklemez, periyodik okunmak için. */
  getSilence: () => SilenceSnapshot;
}

/**
 * Konuşma sayılacak en düşük seviye (`readLevel` ölçeği, 0-100). Sessizlik
 * takibi (otomatik durdurma) buna bakıyor.
 */
const SOUND_LEVEL = Math.round((22 / 255) * 100 * 1.6);

/** Deepgram'a akan dilimlerin uzunluğu — kısa = daha canlı ara sonuç. */
const LIVE_TIMESLICE_MS = 250;

/** Kopan bağlantıyı yeniden açma denemeleri arasındaki bekleme. */
const RECONNECT_DELAY_MS = 1000;
/** Dakikada en fazla bu kadar yeniden bağlanma — ağ tamamen gittiyse döngüye girme. */
const MAX_RECONNECTS_PER_MINUTE = 5;

const SPEAKER_BY_SOURCE: Record<LiveSource, string> = {
  mic: SPEAKER_ME,
  system: SPEAKER_GUEST,
};

/** Kaynak başına canlı akış durumu. */
interface LiveStream {
  stream: MediaStream;
  recorder: MediaRecorder | null;
  /** Bu Deepgram bağlantısı açıldığında kaydın kaçıncı saniyesindeydik. */
  offsetSeconds: number;
  /** Dilimlerin IPC'ye sırayla gitmesi için zincir. */
  sendChain: Promise<void>;
  /** Kesinleşmiş ama henüz cümle sonu gelmemiş metin. */
  utteranceText: string;
  utteranceStart: number | null;
  /** Deepgram'ın o anki ara (kesinleşmemiş) sonucu. */
  interimText: string;
  reconnectTimes: number[];
}

/**
 * Sessizlik yoklaması aralığı. `requestAnimationFrame` tabanlı seviye
 * döngüsüne bağlanmıyor: pencere küçültülünce rAF hiç çalışmıyor, oysa
 * toplantı sırasında uygulamanın küçültülmüş durması olağan durum.
 */
const SILENCE_PROBE_MS = 500;

/** Sistem sesinin "karşı taraf konuştu" sayılması için gereken toplam süre. */
const SYSTEM_HEARD_MIN_MS = 10_000;

// Electron'un eski (mandatory-constraint) masaüstü ses yakalama API'si — DOM
// tipleri bunu tanımıyor, bu yüzden constraint objesi elle genişletiliyor.
type DesktopAudioConstraints = MediaTrackConstraints & {
  mandatory: { chromeMediaSource: 'desktop'; chromeMediaSourceId?: string };
};

async function getSystemAudioStream(): Promise<MediaStream> {
  const sources = await window.zabit.getDesktopSources();
  const primary = sources[0];
  if (!primary) {
    throw new Error(
      t(
        'Sistem sesi kaynağı bulunamadı (ekran paylaşımı izni gerekebilir).',
        'No system audio source was found (screen sharing permission may be needed).'
      )
    );
  }

  const audioConstraint: DesktopAudioConstraints = { mandatory: { chromeMediaSource: 'desktop' } };
  const videoConstraint: DesktopAudioConstraints = {
    mandatory: { chromeMediaSource: 'desktop', chromeMediaSourceId: primary.id },
  };

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: audioConstraint,
    video: videoConstraint,
  });

  // Sadece sesle ilgileniyoruz. Track'i durdurmak yetmiyor: durdurulmuş bir
  // video track akışın içinde kalmaya devam ediyor ve `MediaRecorder`a
  // `audio/webm;codecs=opus` verildiğinde Chromium akışta video görüp
  // NotSupportedError fırlatabiliyor — canlı transkript döngüsü hiç
  // başlamıyordu. Bu yüzden durdurup akıştan da çıkarıyoruz.
  stream.getVideoTracks().forEach((track) => {
    track.stop();
    stream.removeTrack(track);
  });
  return stream;
}

/**
 * Web: paylaşılan toplantı sekmesinin sesi. Tarayıcılar ses için de ekran
 * paylaşımı istiyor (yalnız ses istenemiyor); görüntü izi hemen atılıyor.
 * Kullanıcı "Sekme sesini de paylaş"ı kapattıysa ses izi gelmiyor.
 */
async function getTabAudioStream(): Promise<MediaStream> {
  const options = {
    video: true,
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    // Chrome/Edge'e özgü ipuçları: sekme paylaşımını öne çıkar, TalkNote'un kendi sekmesini gösterme.
    preferCurrentTab: false,
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
    systemAudio: 'include',
  } as DisplayMediaStreamOptions;
  const stream = await navigator.mediaDevices.getDisplayMedia(options);
  stream.getVideoTracks().forEach((track) => {
    track.stop();
    stream.removeTrack(track);
  });
  if (stream.getAudioTracks().length === 0) {
    throw new Error(
      t(
        'Toplantı sekmesinin sesi paylaşılmadı (“Sekme sesini de paylaş” kapalıydı). Yalnızca mikrofon kaydediliyor.',
        'The meeting tab’s audio was not shared (“Also share tab audio” was off). Recording the microphone only.'
      )
    );
  }
  return stream;
}

/** Mikrofon; izin/aygıt hatası kullanıcının anlayacağı dille. */
async function getMicStream(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (err) {
    const name = (err as DOMException)?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      throw new Error(
        IS_WEB
          ? t(
              'Mikrofon izni verilmedi. Adres çubuğundaki kilit simgesinden mikrofona izin verip tekrar dene.',
              'Microphone access was denied. Allow the microphone from the lock icon in the address bar and try again.'
            )
          : t('Mikrofon izni verilmedi.', 'Microphone access was denied.')
      );
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      throw new Error(t('Mikrofon bulunamadı. Bir mikrofon bağlayıp tekrar dene.', 'No microphone was found. Connect one and try again.'));
    }
    if (name === 'NotReadableError') {
      throw new Error(t('Mikrofon başka bir uygulama tarafından kullanılıyor olabilir.', 'The microphone may be in use by another app.'));
    }
    throw err;
  }
}

/**
 * Kayıt akışları. Masaüstünde mikrofon + sistem sesi (hep). Web'de mikrofon,
 * istenirse paylaşılan sekmenin sesi; sekme paylaşılamazsa kayıt yalnızca
 * mikrofonla sürer ve nedeni `warning`'de döner. `systemOnly`'de mikrofon
 * hiç istenmez; sistem sesi (web'de sekme) alınamazsa hata.
 */
async function acquireStreams(
  captureSystemAudio: boolean,
  systemOnly: boolean,
  onNativeFailure: (message: string) => void,
  onNativePause: (change: NativePauseChange) => void
): Promise<{ micStream: MediaStream | null; systemStream: MediaStream | null; warning: string | null }> {
  if (!IS_WEB) {
    if (systemOnly) return { micStream: null, systemStream: await getSystemAudioStream(), warning: null };
    const [micStream, systemStream] = await Promise.all([getMicStream(), getSystemAudioStream()]);
    return { micStream, systemStream, warning: null };
  }
  // Mobil: telefonun kaydedicisi (ekran kilitliyken de sürer). Telefonda sistem sesi yok.
  const nativeMic = window.zabit.nativeMic;
  if (nativeMic && !systemOnly) {
    return { micStream: await nativeMic.start(onNativeFailure, onNativePause), systemStream: null, warning: null };
  }
  // Canlı döküm (Deepgram) ve kayıt webm/opus bekliyor; eski Safari bunu üretemiyor.
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
    throw new Error(
      t(
        'Bu tarayıcı kayıt almayı desteklemiyor. Chrome, Edge ya da Firefox kullanabilir; ya da ses dosyası yükleyip toplantıya bot gönderebilirsin.',
        'This browser can’t record. Use Chrome, Edge or Firefox — or upload an audio file and send a bot to your meetings.'
      )
    );
  }
  if (systemOnly) {
    try {
      return { micStream: null, systemStream: await getTabAudioStream(), warning: null };
    } catch (err) {
      if ((err as DOMException)?.name !== 'NotAllowedError') throw err;
      throw new Error(
        t('Toplantı sekmesi paylaşılmadı; kayıt başlamadı.', 'The meeting tab was not shared, so the recording didn’t start.')
      );
    }
  }
  // İkisi aynı anda istenir: paylaşım penceresi tıklamanın içinde açılmalı.
  const [mic, tab] = await Promise.allSettled([
    getMicStream(),
    captureSystemAudio ? getTabAudioStream() : Promise.resolve(null),
  ]);
  if (mic.status === 'rejected') {
    if (tab.status === 'fulfilled') tab.value?.getTracks().forEach((track) => track.stop());
    throw mic.reason;
  }
  if (tab.status === 'rejected') {
    const cancelled = (tab.reason as DOMException)?.name === 'NotAllowedError';
    return {
      micStream: mic.value,
      systemStream: null,
      warning: cancelled
        ? t('Toplantı sekmesi paylaşılmadı; yalnızca mikrofon kaydediliyor.', 'The meeting tab was not shared; recording the microphone only.')
        : (tab.reason as Error).message,
    };
  }
  return { micStream: mic.value, systemStream: tab.value, warning: null };
}

/** Web'de kayıt parçalarının tarayıcıya yazılma aralığı (sekme kapanırsa kurtarılır). */
const PERSIST_TIMESLICE_MS = 10_000;

/** Analyser'ın o anki ortalama genliği, 0-100 ölçeğine çekilmiş. */
function readLevel(analyser: AnalyserNode, data: Uint8Array<ArrayBuffer>): number {
  analyser.getByteFrequencyData(data);
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i];
  const average = sum / data.length;
  return Math.min(100, Math.round((average / 255) * 100 * 1.6));
}

export function formatTimestamp(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function useRecorder(): UseRecorderResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [micLevel, setMicLevel] = useState(0);
  const [systemLevel, setSystemLevel] = useState(0);
  const [hasSystemAudio, setHasSystemAudio] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [liveTranscript, setLiveTranscript] = useState<SpeakerSegment[]>([]);
  const [pendingLines, setPendingLines] = useState<PendingLine[]>([]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const systemAnalyserRef = useRef<AnalyserNode | null>(null);
  const meterDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const stopStreamsRef = useRef<(() => void) | null>(null);
  // start() üst üste (hızlı aç/kapat) tetiklenirse eski oturumun donanımını
  // bırakmadan yenisi açılmasın diye basit bir kilit.
  const isStartingRef = useRef(false);

  const secondsRef = useRef(0);
  /**
   * Süre duvar saatinden: sayaç her saniye bir artırılsaydı, zamanlayıcı
   * kısıldığında (küçültülmüş pencere, arka plandaki sekme) süre ve
   * transkript zaman damgaları geride kalırdı.
   */
  const startedAtRef = useRef(0);
  const pausedMsRef = useRef(0);
  const pausedAtRef = useRef<number | null>(null);
  // Web: kayıt parçalarının tarayıcıya yazıldığı oturum ve sıra numarası.
  const recordingIdRef = useRef<string | null>(null);
  const chunkSeqRef = useRef(0);
  const isPausedRef = useRef(false);
  const transcribingActiveRef = useRef(false);
  const liveTranscriptRef = useRef<SpeakerSegment[]>([]);
  const languageRef = useRef<TranscribeLanguage>('auto');

  // Deepgram canlı akışı: oturum kimliği, kaynak başına akış, olay aboneliği.
  const sessionIdRef = useRef<string | null>(null);
  const liveStreamsRef = useRef(new Map<LiveSource, LiveStream>());
  const unsubscribeLiveRef = useRef<(() => void) | null>(null);

  // Sessizlik takibi (bkz. SilenceSnapshot).
  const silenceProbeRef = useRef<number | null>(null);
  const lastMicSoundAtRef = useRef(0);
  const lastSystemSoundAtRef = useRef(0);
  const systemSoundMsRef = useRef(0);

  // Konuşmacı başına ekrandaki soluk (oluşmakta olan) satır.
  const pendingRef = useRef(new Map<string, string>());

  // Mobil: telefon tarafında duraklama (görüşme, bildirim düğmesi) — setPaused tanımlanınca bağlanır.
  const nativePauseRef = useRef<(change: NativePauseChange) => void>(() => {});

  const meterLoop = useCallback(() => {
    const mic = micAnalyserRef.current;
    const system = systemAnalyserRef.current;
    const data = meterDataRef.current;
    if (!mic || !data) return;

    setMicLevel(readLevel(mic, data));
    // Web'de yalnızca mikrofonla kayıtta sistem sesi yok.
    setSystemLevel(system ? readLevel(system, data) : 0);
    rafRef.current = requestAnimationFrame(meterLoop);
  }, []);

  /** Kaydın başından bu yana geçen süre (duraklatmalar hariç), saniye. */
  const elapsedSeconds = useCallback(() => {
    if (!startedAtRef.current) return secondsRef.current;
    const now = Date.now();
    const paused = pausedMsRef.current + (pausedAtRef.current !== null ? now - pausedAtRef.current : 0);
    return Math.max(0, Math.floor((now - startedAtRef.current - paused) / 1000));
  }, []);

  const startClock = useCallback(() => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      secondsRef.current = elapsedSeconds();
      setSeconds(secondsRef.current);
    }, 1000);
  }, [elapsedSeconds]);

  const appendSegment = useCallback((speaker: string, text: string, atSeconds: number) => {
    const seg: SpeakerSegment = {
      id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      speaker,
      timestamp: formatTimestamp(atSeconds),
      seconds: atSeconds,
      text,
    };
    // Sıralama kararlı: aynı saniyeye düşen cümleler eklenme sırasını korur,
    // yani bir parçadan çıkan cümleler birbirinin önüne geçmez.
    liveTranscriptRef.current = [...liveTranscriptRef.current, seg].sort((a, b) => a.seconds - b.seconds);
    setLiveTranscript(liveTranscriptRef.current);
  }, []);

  const setPendingFor = useCallback((speaker: string, text: string) => {
    const map = pendingRef.current;
    if (text) map.set(speaker, text);
    else map.delete(speaker);
    setPendingLines(Array.from(map, ([s, t]) => ({ speaker: s, text: t })));
  }, []);

  /** Kaynağın soluk satırını (kesinleşmiş + ara metin) günceller. */
  const refreshPending = useCallback((source: LiveSource) => {
    const live = liveStreamsRef.current.get(source);
    const text = live ? [live.utteranceText, live.interimText].filter(Boolean).join(' ') : '';
    setPendingFor(SPEAKER_BY_SOURCE[source], text);
  }, [setPendingFor]);

  /**
   * Kaynağın biriken cümlesini kalıcı satıra çevirir. `includeInterim`
   * kayıt biterken kullanılıyor: kesinleşmemiş son sözler kaybolmasın.
   */
  const flushUtterance = useCallback((source: LiveSource, includeInterim = false) => {
    const live = liveStreamsRef.current.get(source);
    if (!live) return;
    const text = [live.utteranceText, includeInterim ? live.interimText : '']
      .filter(Boolean)
      .join(' ')
      .trim();
    if (text) {
      appendSegment(SPEAKER_BY_SOURCE[source], text, live.utteranceStart ?? live.offsetSeconds);
    }
    live.utteranceText = '';
    live.utteranceStart = null;
    if (includeInterim) live.interimText = '';
    refreshPending(source);
  }, [appendSegment, refreshPending]);

  /** Tüm kaynakların yarım cümlelerini kalıcı satıra çevirir. */
  const flushPending = useCallback(() => {
    for (const source of liveStreamsRef.current.keys()) flushUtterance(source, true);
    pendingRef.current.clear();
    setPendingLines([]);
  }, [flushUtterance]);

  /**
   * Bir kaynak için Deepgram bağlantısını açar ve o kaynağa yeni bir
   * `MediaRecorder` bağlar. Her bağlantı yeni bir kaydediciyle başlamalı:
   * Deepgram webm başlığını yalnızca akışın ilk diliminden okuyabiliyor.
   */
  const openSourceStream = useCallback(async (source: LiveSource) => {
    const sessionId = sessionIdRef.current;
    const live = liveStreamsRef.current.get(source);
    if (!sessionId || !live || !transcribingActiveRef.current) return;

    const previous = live.recorder;
    live.recorder = null;
    if (previous && previous.state !== 'inactive') {
      previous.ondataavailable = null;
      try { previous.stop(); } catch { /* zaten durmuş */ }
    }

    live.offsetSeconds = elapsedSeconds();
    await window.zabit.liveOpen(sessionId, source, languageRef.current);
    if (sessionIdRef.current !== sessionId || !transcribingActiveRef.current) return;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(live.stream, { mimeType: 'audio/webm;codecs=opus' });
    } catch {
      // Kaynak kaybolduysa (aygıt çekildi) bu kaynağın transkripti durur,
      // karışık kayıt devam eder.
      return;
    }

    recorder.ondataavailable = (e) => {
      if (e.data.size === 0) return;
      live.sendChain = live.sendChain.then(async () => {
        // Kaydedici değiştirildiyse eski dilimler yeni bağlantıya gitmesin.
        if (live.recorder !== recorder || sessionIdRef.current !== sessionId) return;
        window.zabit.liveAudio(sessionId, source, await e.data.arrayBuffer());
      });
    };
    live.recorder = recorder;
    recorder.start(LIVE_TIMESLICE_MS);
    if (isPausedRef.current) recorder.pause();
  }, [elapsedSeconds]);

  const handleLiveEvent = useCallback((event: LiveTranscriptEvent) => {
    if (event.sessionId !== sessionIdRef.current) return;
    const live = liveStreamsRef.current.get(event.source);
    if (!live) return;

    switch (event.kind) {
      case 'transcript': {
        if (event.isFinal) {
          if (event.text) {
            live.utteranceText = [live.utteranceText, event.text].filter(Boolean).join(' ');
            live.utteranceStart ??= live.offsetSeconds + event.start;
          }
          live.interimText = '';
          if (event.speechFinal) flushUtterance(event.source);
          else refreshPending(event.source);
        } else {
          live.interimText = event.text;
          refreshPending(event.source);
        }
        break;
      }
      case 'utteranceEnd':
        flushUtterance(event.source);
        break;
      case 'closed': {
        if (!transcribingActiveRef.current) break;
        flushUtterance(event.source, true);
        const now = Date.now();
        live.reconnectTimes = live.reconnectTimes.filter((t) => now - t < 60_000);
        if (live.reconnectTimes.length >= MAX_RECONNECTS_PER_MINUTE) {
          setError(
            t(
              'Canlı transkript bağlantısı sürekli kopuyor; ses kaydı devam ediyor.',
              'The live transcript connection keeps dropping; audio recording continues.'
            )
          );
          break;
        }
        live.reconnectTimes.push(now);
        window.setTimeout(() => {
          void openSourceStream(event.source).catch(() => {});
        }, RECONNECT_DELAY_MS);
        break;
      }
      case 'error':
        setError(event.message);
        break;
      case 'notice':
        setNotice(event.message);
        break;
    }
  }, [flushUtterance, refreshPending, openSourceStream]);

  /**
   * Canlı akışı kapatır. `graceful` ise son dilimler gönderilip Deepgram'ın
   * son sonuçları beklenir (kayıt bitirilirken); değilse hemen kesilir.
   */
  const closeLiveStreams = useCallback(async (graceful: boolean) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;

    await Promise.all(
      [...liveStreamsRef.current.entries()].map(async ([source, live]) => {
        const recorder = live.recorder;
        if (recorder && recorder.state !== 'inactive') {
          if (graceful) {
            await new Promise<void>((resolve) => {
              recorder.onstop = () => resolve();
              recorder.onerror = () => resolve();
              try { recorder.stop(); } catch { resolve(); }
            });
            await live.sendChain;
          } else {
            recorder.ondataavailable = null;
            try { recorder.stop(); } catch { /* zaten durmuş */ }
          }
        }
        live.recorder = null;
        const closing = window.zabit.liveClose(sessionId, source).catch(() => {});
        if (graceful) await closing;
      })
    );

    transcribingActiveRef.current = false;
    sessionIdRef.current = null;
    unsubscribeLiveRef.current?.();
    unsubscribeLiveRef.current = null;
  }, []);

  // Donanımı (mikrofon/sistem sesi track'leri, AudioContext, timer'lar) serbest
  // bırakır — dosyayı KAYDETMEZ, sadece temizlik yapar.
  const teardown = useCallback(() => {
    // Kayıt düzgün bitirildiyse akış zaten kapalı; değilse (iptal, hata) kes.
    void closeLiveStreams(false);
    transcribingActiveRef.current = false;
    stopStreamsRef.current?.();
    stopStreamsRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    micAnalyserRef.current = null;
    systemAnalyserRef.current = null;
    meterDataRef.current = null;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (silenceProbeRef.current !== null) {
      window.clearInterval(silenceProbeRef.current);
      silenceProbeRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setIsRecording(false);
    setIsPaused(false);
    setMicLevel(0);
    setSystemLevel(0);
  }, [closeLiveStreams]);

  const start = useCallback(
    async (options: StartOptions) => {
      if (isStartingRef.current) return;
      isStartingRef.current = true;
      setError(null);
      setNotice(null);
      try {
        // Önceki oturum düzgün durdurulmadan yeni bir start() geldiyse (hızlı
        // aç/kapat), önce onu temizle — yoksa mikrofon/sistem sesi track'leri
        // sızar ve chunksRef karışır.
        if (mediaRecorderRef.current) {
          const stale = mediaRecorderRef.current;
          if (stale.state !== 'inactive') stale.stop();
          teardown();
        }

        // Akışlar dil tercihi beklenmeden istenir (web: paylaşım penceresi tıklamanın içinde açılmalı).
        const streamsPromise = acquireStreams(options.captureSystemAudio === true, options.systemOnly === true, setError, (change) =>
          nativePauseRef.current(change)
        );
        // Dil beklenirken reddedilirse "işlenmemiş" sayılmasın; hata aşağıdaki await'te yakalanıyor.
        streamsPromise.catch(() => {});
        languageRef.current = await options.language;
        const { micStream, systemStream, warning } = await streamsPromise;

        // Akışlar elde edilir edilmez serbest bırakma yolunu kaydet. Daha önce
        // bu satır AudioContext kurulumundan SONRA geliyordu: kurulum hata
        // verirse (ör. ses aygıtı meşgul) teardown() akışları bulamıyor,
        // mikrofon açık kalıyordu — işletim sistemindeki kayıt göstergesi
        // uygulama boştayken bile yanıyordu.
        stopStreamsRef.current = () => {
          micStream?.getTracks().forEach((t) => t.stop());
          systemStream?.getTracks().forEach((t) => t.stop());
        };
        if (warning) setError(warning);
        setHasSystemAudio(systemStream !== null);
        // Web: kullanıcı tarayıcının "Paylaşımı durdur" çubuğuna basarsa kayıt mikrofonla sürer
        // (yalnızca sistem sesi kaydında artık ses gelmiyor).
        systemStream?.getAudioTracks()[0]?.addEventListener('ended', () => {
          setHasSystemAudio(false);
          setError(
            micStream
              ? t(
                  'Toplantı sekmesinin paylaşımı durduruldu; kayıt yalnızca mikrofonla sürüyor.',
                  'Sharing of the meeting tab stopped; recording continues with the microphone only.'
                )
              : t(
                  'Toplantı sekmesinin paylaşımı durduruldu; artık ses alınmıyor. Kaydı bitirebilirsin.',
                  'Sharing of the meeting tab stopped, so no audio is coming in. You can finish the recording.'
                )
          );
        });

        const audioContext = new AudioContext();
        const destination = audioContext.createMediaStreamDestination();
        if (micStream) audioContext.createMediaStreamSource(micStream).connect(destination);
        if (systemStream) audioContext.createMediaStreamSource(systemStream).connect(destination);

        // Mikrofon ve sistem sesi ayrı ayrı ölçülüyor: kayıt ekranı iki bağımsız
        // seviye çubuğu gösteriyor ve sessizlik filtresi her kaynağı kendi
        // enerjisine göre değerlendiriyor. Aynı stream'den birden fazla kaynak
        // düğümü oluşturmak sorun değil. Yalnızca sistem sesi kaydında mikrofon
        // ölçeri boş kalır (hep sessiz).
        const micAnalyser = audioContext.createAnalyser();
        micAnalyser.fftSize = 256;
        if (micStream) audioContext.createMediaStreamSource(micStream).connect(micAnalyser);

        let systemAnalyser: AnalyserNode | null = null;
        if (systemStream) {
          systemAnalyser = audioContext.createAnalyser();
          systemAnalyser.fftSize = 256;
          audioContext.createMediaStreamSource(systemStream).connect(systemAnalyser);
        }

        audioContextRef.current = audioContext;
        micAnalyserRef.current = micAnalyser;
        systemAnalyserRef.current = systemAnalyser;
        meterDataRef.current = new Uint8Array(micAnalyser.frequencyBinCount);

        chunksRef.current = [];
        chunkSeqRef.current = 0;
        const recordingId = IS_WEB ? crypto.randomUUID() : null;
        recordingIdRef.current = recordingId;
        const recorder = new MediaRecorder(destination.stream, { mimeType: 'audio/webm;codecs=opus' });
        recorder.ondataavailable = (e) => {
          if (e.data.size === 0) return;
          chunksRef.current.push(e.data);
          // Web: parça hemen tarayıcı deposuna — sekme kapanır ya da çökerse kayıt kurtarılır.
          if (recordingId) window.zabit.persistRecordingChunk?.(recordingId, chunkSeqRef.current++, e.data);
        };
        mediaRecorderRef.current = recorder;
        // Masaüstünde tek parça (dosya kayıt bitince diske yazılıyor); web'de 10 sn'lik parçalar.
        if (IS_WEB) recorder.start(PERSIST_TIMESLICE_MS);
        else recorder.start();

        setSeconds(0);
        secondsRef.current = 0;
        startedAtRef.current = Date.now();
        pausedMsRef.current = 0;
        pausedAtRef.current = null;
        setIsPaused(false);
        isPausedRef.current = false;
        liveTranscriptRef.current = [];
        setLiveTranscript([]);
        pendingRef.current.clear();
        setPendingLines([]);

        startClock();
        rafRef.current = requestAnimationFrame(meterLoop);

        const probeStartedAt = Date.now();
        lastMicSoundAtRef.current = probeStartedAt;
        lastSystemSoundAtRef.current = probeStartedAt;
        systemSoundMsRef.current = 0;
        // Ayrı bir örnek tamponu: meterLoop ile aynı tamponu paylaşmak JS tek
        // iş parçacıklı olduğu için yarış yaratmaz ama okunurluğu bozar.
        const probeData = new Uint8Array(micAnalyser.frequencyBinCount);
        silenceProbeRef.current = window.setInterval(() => {
          if (isPausedRef.current) return;
          const now = Date.now();
          if (readLevel(micAnalyser, probeData) >= SOUND_LEVEL) lastMicSoundAtRef.current = now;
          if (systemAnalyser && readLevel(systemAnalyser, probeData) >= SOUND_LEVEL) {
            lastSystemSoundAtRef.current = now;
            systemSoundMsRef.current += SILENCE_PROBE_MS;
          }
        }, SILENCE_PROBE_MS);

        setIsRecording(true);

        // Canlı transkript: mikrofon ve sistem sesi ayrı Deepgram
        // bağlantılarından akıyor — konuşmacı ayrımı kaynağa göre.
        const makeLive = (stream: MediaStream): LiveStream => ({
          stream,
          recorder: null,
          offsetSeconds: 0,
          sendChain: Promise.resolve(),
          utteranceText: '',
          utteranceStart: null,
          interimText: '',
          reconnectTimes: [],
        });
        const sources: Array<[LiveSource, MediaStream]> = [];
        if (micStream) sources.push(['mic', micStream]);
        if (systemStream) sources.push(['system', systemStream]);
        liveStreamsRef.current = new Map(sources.map(([source, stream]) => [source, makeLive(stream)]));
        sessionIdRef.current = `live-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        unsubscribeLiveRef.current = window.zabit.onLiveEvent(handleLiveEvent);
        transcribingActiveRef.current = true;
        // Transkript bağlantısı kurulamasa bile ses kaydı sürmeli.
        void Promise.all(sources.map(([source]) => openSourceStream(source))).catch((err) =>
          setError(t(`Canlı transkript başlatılamadı: ${(err as Error).message}`, `Could not start the live transcript: ${(err as Error).message}`))
        );
      } catch (err) {
        if (recordingIdRef.current) void window.zabit.discardRecordingChunks?.(recordingIdRef.current);
        recordingIdRef.current = null;
        void window.zabit.nativeMic?.discard();
        teardown();
        setError((err as Error).message);
        throw err;
      } finally {
        isStartingRef.current = false;
      }
    },
    [meterLoop, teardown, handleLiveEvent, openSourceStream, startClock]
  );

  const setLanguage = useCallback((language: TranscribeLanguage) => {
    if (languageRef.current === language) return;
    languageRef.current = language;
    // Deepgram'da dil bağlantı parametresi — süren kayıtta bağlantıları
    // yeni dille yeniden aç. Yarım cümleler önce kalıcı satıra düşsün.
    if (!sessionIdRef.current || !transcribingActiveRef.current) return;
    for (const source of liveStreamsRef.current.keys()) {
      flushUtterance(source, true);
      void openSourceStream(source).catch(() => {});
    }
  }, [flushUtterance, openSourceStream]);

  /**
   * Kaydı duraklatır ya da sürdürür. `fromNative`: değişiklik telefondan geldi (görüşme,
   * bildirimdeki düğme) — telefon zaten o durumda, ona yeniden söylenmez; yoksa görüşme
   * bitince kendiliğinden sürmesi gereken kayıt kullanıcı duraklatması sayılırdı.
   */
  const setPaused = useCallback((paused: boolean, fromNative: boolean) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    if (paused && recorder.state === 'recording') {
      recorder.pause();
      if (!fromNative) void window.zabit.nativeMic?.pause();
      // Duraklatmada Deepgram'a ses gitmiyor; bağlantıyı ana süreç KeepAlive
      // ile canlı tutuyor.
      for (const live of liveStreamsRef.current.values()) {
        if (live.recorder?.state === 'recording') live.recorder.pause();
      }
      isPausedRef.current = true;
      pausedAtRef.current = Date.now();
      secondsRef.current = elapsedSeconds();
      setSeconds(secondsRef.current);
      // Kimlikler temizlendikten sonra null'lanmalı: aksi hâlde teardown()
      // ya da ikinci bir duraklatma çoktan geçersiz olmuş bir kimliği
      // iptal etmeye çalışıyor ve devam ettirmede yeni sayaç eskisinin
      // üstüne biniyordu (süre iki kat hızlı akıyordu).
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setMicLevel(0);
      setSystemLevel(0);
      setIsPaused(true);
    } else if (!paused && recorder.state === 'paused') {
      recorder.resume();
      if (!fromNative) void window.zabit.nativeMic?.resume();
      for (const live of liveStreamsRef.current.values()) {
        if (live.recorder?.state === 'paused') live.recorder.resume();
      }
      isPausedRef.current = false;
      // Duraklatmada geçen süre ne kayıt süresine ne sessizliğe sayılsın.
      if (pausedAtRef.current !== null) pausedMsRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
      lastMicSoundAtRef.current = Date.now();
      lastSystemSoundAtRef.current = Date.now();
      startClock();
      rafRef.current = requestAnimationFrame(meterLoop);
      setIsPaused(false);
    }
  }, [meterLoop, elapsedSeconds, startClock]);

  const togglePause = useCallback(() => {
    setPaused(mediaRecorderRef.current?.state === 'recording', false);
  }, [setPaused]);

  const onNativePause = useCallback(
    (change: NativePauseChange) => {
      setPaused(change.paused, true);
      setNotice(
        change.interrupted
          ? t(
              'Telefon görüşmesi sırasında kayıt duraklatıldı; görüşme bitince kendiliğinden sürecek.',
              'Recording paused during the phone call; it resumes on its own when the call ends.'
            )
          : null
      );
    },
    [setPaused]
  );
  useEffect(() => {
    nativePauseRef.current = onNativePause;
  }, [onNativePause]);

  const getSilence = useCallback((): SilenceSnapshot => {
    const now = Date.now();
    return {
      micSilentMs: now - lastMicSoundAtRef.current,
      systemSilentMs: now - lastSystemSoundAtRef.current,
      systemEverHeard: systemSoundMsRef.current >= SYSTEM_HEARD_MIN_MS,
    };
  }, []);

  const stop = useCallback(async (): Promise<RecordingResult | null> => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return null;

    const finalSeconds = elapsedSeconds();
    const recordingId = recordingIdRef.current;

    // `stop()` zaten durmuş bir kaydediciye çağrılırsa InvalidStateError
    // fırlatır ve `onstop` hiç tetiklenmez — söz sonsuza kadar askıda
    // kalıyor, arayüz "Kaydediliyor…" üzerinde donuyordu. Durum önce
    // kontrol ediliyor, çağrının kendisi de korumaya alındı.
    const blob = await new Promise<Blob>((resolve) => {
      const finish = () => resolve(new Blob(chunksRef.current, { type: 'audio/webm' }));

      if (recorder.state === 'inactive') {
        finish();
        return;
      }

      recorder.onstop = finish;
      recorder.onerror = finish;
      try {
        recorder.stop();
      } catch {
        finish();
      }
    });

    // Mobil: arşive telefonun kesintisiz kaydı gider — tarayıcınınki uygulama
    // arka plandayken kesilmiş olabilir. Alınamazsa tarayıcınınki.
    const nativeAudio = window.zabit.nativeMic ? await window.zabit.nativeMic.stop() : null;

    // Son dilimler Deepgram'a gitsin ve son sonuçlar gelsin — donanım
    // kapatılmadan ÖNCE, yoksa kullanıcının son cümleleri kaybolur.
    await closeLiveStreams(true).catch(() => {});
    flushPending();
    teardown();
    const finalTranscript = liveTranscriptRef.current;

    // Bir sonraki kaydın cümleleri bu kaydın kuyruğuna eklenmesin.
    liveStreamsRef.current = new Map();
    mediaRecorderRef.current = null;

    const buffer = await (nativeAudio ?? blob).arrayBuffer();
    // Web: kayıt kaydedilince tarayıcıdaki ara parçalar silinir (bkz. persistRecordingChunk).
    const filePath = await window.zabit.saveRecording(buffer, recordingId ?? undefined, nativeAudio?.type);
    recordingIdRef.current = null;
    const startedAtISO = new Date(startedAtRef.current || Date.now()).toISOString();
    startedAtRef.current = 0;
    return { filePath, durationSeconds: finalSeconds, transcript: finalTranscript, startedAtISO, endedAtISO: new Date().toISOString() };
  }, [teardown, flushPending, closeLiveStreams, elapsedSeconds]);

  // İptal — kaydı durdurur ama diske YAZMAZ. "İptal" derken kullanıcı gerçekten
  // hiçbir şeyin saklanmamasını bekler.
  const discard = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
        recorder.onerror = () => resolve();
        try {
          recorder.stop();
        } catch {
          resolve();
        }
      });
    }
    await window.zabit.nativeMic?.discard();
    teardown();
    // Web: iptal edilen kaydın tarayıcıdaki parçaları da gitsin (kurtarılacak bir şey yok).
    if (recordingIdRef.current) await window.zabit.discardRecordingChunks?.(recordingIdRef.current);
    recordingIdRef.current = null;
    startedAtRef.current = 0;
    setError(null);
    setNotice(null);
    liveStreamsRef.current = new Map();
    pendingRef.current.clear();
    setPendingLines([]);
    liveTranscriptRef.current = [];
    setLiveTranscript([]);
  }, [teardown]);

  return {
    isRecording,
    isPaused,
    seconds,
    micLevel,
    systemLevel,
    hasSystemAudio,
    error,
    notice,
    liveTranscript,
    pendingLines,
    start,
    stop,
    discard,
    togglePause,
    setLanguage,
    getSilence,
  };
}
